import { ROLES } from '../constants/roles';

export interface StaffProfile {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  houseId?: string;
  commission?: number;
  collected?: number;
  pending?: number;
  coinsIn?: number;
  plays?: number;
}

export interface WindowBucket {
  count: number;
  volume: number;
  earnings: number;
}

export interface ListerAgreement {
  role: 'lister';
  rate: number;
  volume: number;
  earnings: number;
  playCount: number;
  profileName: string;
  byWindow: {
    day: WindowBucket;
    night: WindowBucket;
  };
}

export interface RunnerAgreement {
  role: 'runner';
  rate: number;
  volumeCollected: number;
  volumePending: number;
  earnings: number;
  potential: number;
  stopCount: number;
  stopsDone: number;
  profileName: string;
}

type PlayLike = { stake?: number; window?: string };
type StopLike = { done?: boolean; amount?: number; expectedAmount?: number; status?: string };

function stopCollected(s: StopLike): boolean {
  return s.done === true || s.status === 'cleared';
}

function stopAmount(s: StopLike): number {
  return Number(s.expectedAmount ?? s.amount ?? 0);
}

export function resolveStaffProfile(
  viewer: { id?: string; email?: string; role?: string; houseId?: string; name?: string } | null | undefined,
  staffUsers: StaffProfile[],
): StaffProfile | null {
  if (!viewer) return null;

  const byId = staffUsers.find((u) => u.id === viewer.id);
  if (byId) return byId;

  if (viewer.email) {
    const byEmail = staffUsers.find((u) => u.email === viewer.email);
    if (byEmail) return byEmail;
  }

  const sameHouse = staffUsers.filter(
    (u) => u.role === viewer.role && u.houseId === viewer.houseId,
  );
  if (sameHouse.length === 1) return sameHouse[0];
  if (sameHouse.length > 1 && viewer.name) {
    const byName = sameHouse.find((u) => u.name === viewer.name);
    if (byName) return byName;
  }

  return null;
}

export function computeCommissionAmount(volume: number, ratePercent: number): number {
  return Math.round(Number(volume ?? 0) * Number(ratePercent ?? 0) / 100);
}

export function summarizePlaysByWindow(plays: PlayLike[]) {
  const empty = () => ({ count: 0, volume: 0 });
  const day = empty();
  const night = empty();

  for (const play of plays) {
    const bucket = play.window === 'night' ? night : day;
    bucket.count += 1;
    bucket.volume += Number(play.stake ?? 0);
  }

  return {
    day,
    night,
    total: {
      count: day.count + night.count,
      volume: day.volume + night.volume,
    },
  };
}

export function buildListerAgreement(
  viewer: { name?: string },
  profile: StaffProfile | null,
  plays: PlayLike[],
): ListerAgreement {
  const byWindowRaw = summarizePlaysByWindow(plays);
  const rate = Number(profile?.commission ?? 8);

  const mapBucket = (bucket: { count: number; volume: number }): WindowBucket => ({
    ...bucket,
    earnings: computeCommissionAmount(bucket.volume, rate),
  });

  const volume = byWindowRaw.total.volume;

  return {
    role: 'lister',
    rate,
    volume,
    earnings: computeCommissionAmount(volume, rate),
    playCount: byWindowRaw.total.count,
    profileName: profile?.name ?? viewer.name ?? '',
    byWindow: {
      day: mapBucket(byWindowRaw.day),
      night: mapBucket(byWindowRaw.night),
    },
  };
}

export function buildRunnerAgreement(
  viewer: { name?: string },
  profile: StaffProfile | null,
  stops: StopLike[],
): RunnerAgreement {
  const collectedFromStops = stops
    .filter((s) => stopCollected(s))
    .reduce((sum, s) => sum + stopAmount(s), 0);
  const pendingFromStops = stops
    .filter((s) => !stopCollected(s) && s.status !== 'skipped')
    .reduce((sum, s) => sum + stopAmount(s), 0);

  const rate = Number(profile?.commission ?? 4);
  const volumeCollected = Number(profile?.collected ?? collectedFromStops);
  const volumePending = Number(profile?.pending ?? pendingFromStops);

  return {
    role: 'runner',
    rate,
    volumeCollected,
    volumePending,
    earnings: computeCommissionAmount(volumeCollected, rate),
    potential: computeCommissionAmount(volumePending, rate),
    stopCount: stops.length,
    stopsDone: stops.filter((s) => stopCollected(s)).length,
    profileName: profile?.name ?? viewer.name ?? '',
  };
}

export function supportsStaffAgreements(role?: string): boolean {
  return role === ROLES.LISTER || role === ROLES.RUNNER;
}
